import { useState } from 'react'
import { Link } from 'react-router-dom'
import {
  Users, ThermometerSun, Palmtree, ShoppingCart, Clock,
  Send, FileText, TrendingUp, ChevronRight, Loader2, AlertTriangle,
} from 'lucide-react'
import { toast } from 'sonner'
import { PageHeader } from '@/components/layout/page-header'
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog'
import { useDashboardStats } from '@/hooks/useDashboard'
import { useFristenStats } from '@/hooks/useFristen'
import { cn } from '@/lib/utils'

interface DashboardStats {
  presentToday: number
  presentNames: string[]
  sickToday: number
  sickNames: string[]
  onVacationToday: number
  vacationNames: string[]
  pendingOrders: number
  pendingVacationRequests: number
  date: string
}

function StatCard({
  title, value, names, icon: Icon, iconColor, bgColor, link, emptyText,
}: {
  title: string; value: number; names?: string[]; icon: React.ComponentType<{ className?: string }>
  iconColor: string; bgColor: string; link: string; emptyText?: string
}) {
  return (
    <Link to={link}>
      <Card className="h-full transition-shadow hover:shadow-md">
        <CardContent className="p-4 sm:p-6">
          <div className="flex items-start justify-between">
            <div className="min-w-0 flex-1">
              <p className="text-3xl font-bold">{value}</p>
              <p className="mt-1 text-sm text-muted-foreground">{title}</p>
            </div>
            <div className={cn('rounded-lg p-3', bgColor)}>
              <Icon className={cn('h-6 w-6', iconColor)} />
            </div>
          </div>
          {names && names.length > 0 && (
            <div className="mt-3 border-t pt-2">
              <p className="truncate text-xs text-muted-foreground">
                {names.slice(0, 3).join(', ')}
                {names.length > 3 && ` +${names.length - 3}`}
              </p>
            </div>
          )}
          {names && names.length === 0 && emptyText && (
            <div className="mt-3 border-t pt-2">
              <p className="text-xs text-muted-foreground">{emptyText}</p>
            </div>
          )}
        </CardContent>
      </Card>
    </Link>
  )
}

export default function Dashboard() {
  const { data: stats, isLoading: loading } = useDashboardStats()
  const { data: fristenStats } = useFristenStats()
  const [messageModal, setMessageModal] = useState(false)
  const [message, setMessage] = useState('')
  const [sendingMessage, setSendingMessage] = useState(false)

  const handleSendBroadcast = async () => {
    if (!message.trim()) { toast.error('Bitte geben Sie eine Nachricht ein'); return }
    setSendingMessage(true)
    try {
      toast.success('Nachricht gesendet')
      setMessageModal(false)
      setMessage('')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Nachricht konnte nicht gesendet werden')
    } finally {
      setSendingMessage(false)
    }
  }

  if (loading) {
    return <div className="space-y-4"><Skeleton className="h-8 w-48" /><div className="grid grid-cols-2 gap-4 lg:grid-cols-4"><Skeleton className="h-32" /><Skeleton className="h-32" /><Skeleton className="h-32" /><Skeleton className="h-32" /></div></div>
  }

  return (
    <div>
      <PageHeader title="Dashboard" description="Übersicht ueber alle aktuellen Aktivitaeten" />

      {/* Fristen-Warnung: ablaufende Dokumente */}
      {fristenStats && fristenStats.warning_count > 0 && (
        <Link to="/admin/fristen">
          <Card className={cn(
            'mb-4 border transition-shadow hover:shadow-md',
            fristenStats.expired_count > 0 ? 'border-red-200 bg-red-50' : 'border-amber-200 bg-amber-50'
          )}>
            <CardContent className="flex items-center gap-3 p-4">
              <AlertTriangle className={cn('h-5 w-5 shrink-0', fristenStats.expired_count > 0 ? 'text-red-600' : 'text-amber-600')} />
              <p className="flex-1 text-sm font-medium">
                {fristenStats.warning_count} Dokument{fristenStats.warning_count === 1 ? '' : 'e'} in der Fristen-Ablage {fristenStats.warning_count === 1 ? 'muss' : 'müssen'} erneuert werden
                {fristenStats.expired_count > 0 && ` — davon ${fristenStats.expired_count} bereits abgelaufen!`}
              </p>
              <ChevronRight className="h-5 w-5 shrink-0 text-muted-foreground" />
            </CardContent>
          </Card>
        </Link>
      )}

      {/* Mobile Quick-Access */}
      <div className="mb-4 lg:hidden">
        <Link
          to="/admin/uebersicht"
          className="flex items-center justify-between rounded-2xl bg-gradient-to-r from-blue-600 to-blue-700 p-4 text-white shadow-lg active:scale-[0.98]"
        >
          <div className="flex items-center gap-3">
            <div className="rounded-xl bg-white/20 p-2.5"><TrendingUp className="h-6 w-6" /></div>
            <p className="text-base font-bold">Mitarbeiter-Übersicht</p>
          </div>
          <ChevronRight className="h-5 w-5 text-blue-200" />
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="mb-6 grid grid-cols-2 gap-3 sm:gap-4 lg:mb-8 lg:grid-cols-3 xl:grid-cols-4 lg:gap-6">
        <StatCard title="Heute Anwesend" value={stats?.presentToday || 0} names={stats?.presentNames} icon={Users} iconColor="text-green-600" bgColor="bg-green-50" link="/admin/zeiterfassung" emptyText="Noch niemand anwesend" />
        <StatCard title="Heute Krank" value={stats?.sickToday || 0} names={stats?.sickNames} icon={ThermometerSun} iconColor="text-red-600" bgColor="bg-red-50" link="/admin/krankmeldungen" emptyText="Niemand krank gemeldet" />
        <StatCard title="Heute im Urlaub" value={stats?.onVacationToday || 0} names={stats?.vacationNames} icon={Palmtree} iconColor="text-blue-600" bgColor="bg-blue-50" link="/admin/urlaub" emptyText="Niemand im Urlaub" />
        <StatCard title="Offene Bestellungen" value={stats?.pendingOrders || 0} icon={ShoppingCart} iconColor="text-accent-500" bgColor="bg-accent-50" link="/admin/bestellungen?status=submitted" />
        <StatCard title="Offene Urlaubsantraege" value={stats?.pendingVacationRequests || 0} icon={Clock} iconColor="text-yellow-600" bgColor="bg-yellow-50" link="/admin/urlaub?status=pending" />

        {/* Desktop: Übersicht-Widget */}
        <div className="hidden lg:block">
          <Link to="/admin/uebersicht" className="block h-full">
            <Card className="flex h-full items-start justify-between p-6 transition-shadow hover:shadow-md">
              <div>
                <TrendingUp className="mb-2 h-5 w-5" />
                <p className="text-sm font-medium">Mitarbeiter-Übersicht</p>
              </div>
              <div className="rounded-lg bg-blue-50 p-3"><TrendingUp className="h-6 w-6 text-blue-600" /></div>
            </Card>
          </Link>
        </div>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader><CardTitle>Schnellaktionen</CardTitle></CardHeader>
        <CardContent>
          <div className="flex flex-col flex-wrap gap-3 sm:flex-row sm:gap-4">
            <Button onClick={() => setMessageModal(true)}>
              <Send className="h-4 w-4" /> Nachricht an alle senden
            </Button>
            <Link to="/admin/berichte">
              <Button variant="outline"><FileText className="h-4 w-4" /> Tagesbericht generieren</Button>
            </Link>
          </div>
        </CardContent>
      </Card>

      {/* Broadcast Dialog */}
      <Dialog open={messageModal} onOpenChange={setMessageModal}>
        <DialogContent>
          <DialogHeader><DialogTitle>Nachricht an alle senden</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">Diese Nachricht wird an alle Mitarbeiter gesendet.</p>
            <textarea
              className="flex min-h-[120px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
              placeholder="Ihre Nachricht..."
              value={message}
              onChange={(e) => setMessage(e.target.value)}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setMessageModal(false)}>Abbrechen</Button>
            <Button onClick={handleSendBroadcast} disabled={sendingMessage}>
              {sendingMessage && <Loader2 className="h-4 w-4 animate-spin" />} Senden
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
